import React from 'react';
import {
	ExperienceStart,
	ExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-experience-tracker';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types';
import {
	LoadingStateOptions,
	type ServiceQueryResultWithStatus,
} from '@atlassian/jira-software-deployments-service-query-types';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller';
import { useCoreData } from '../core-data';
import type { CoreData } from '../core-data/types';
import { useProjectAnalyticsAttributes } from '../get-project';

const viewDeploymentsExperienceName = 'viewDeployments';

/**
 * This component should be rendered as soon as we know the user intends to view the
 * Jira Software Deployments page.
 * Jira Software Deployments page.
 */
export const ViewDeploymentsExperienceStart = ({
	experienceId,
}: {
	experienceId: string | null;
}) => {
	const appEditions = useAppEditions();
	return (
		<ExperienceStart
			experienceId={experienceId}
			experience={viewDeploymentsExperienceName}
			analyticsSource="deployments"
			edition={appEditions.software}
			application={JIRA_SOFTWARE}
		/>
	);
};

type Attributes = {
	readonly [key: string]: string | number | boolean | null;
};

type OnExperienceFail = (location: string, error: Error, additionalAttributes?: Attributes) => void;

/**
 * The function returned by this hook should be called if an unexpected
 * error occurs, e.g. a JavaScript exception bubbles out of the deployments view.
 */
export const useViewDeploymentsExperienceFail = (): OnExperienceFail =>
	useExperienceFail({
		experience: viewDeploymentsExperienceName,
		attributes: { hasDeployments: true },
	});

/**
 * This component should be rendered in "expected error" scenarios, e.g. the
 * user does not have the correct permissions or application license to view
 * the deployments page.
 */
export const ViewDeploymentsExpectedErrorExperienceSuccessTracker = () => (
	<ExperienceSuccess
		experience={viewDeploymentsExperienceName}
		attributes={{ hasDeployments: true }}
	/>
);

export const isExperienceSuccess = (
	coreDataServiceResult: ServiceQueryResultWithStatus<CoreData>,
): boolean => coreDataServiceResult.loadingState === LoadingStateOptions.READY; // Once loadingState becomes READY, we return true. All other cases are false

/**
 * This component should be rendered within the deployments application. It
 * fires a success event once deployments have been fetched (or if there are
 * no issues/deployments to display).
 */
export const ViewDeploymentsExperienceSuccessTracker = () => {
	const { serviceResult: coreDataServiceResult } = useCoreData();
	const attributes = useProjectAnalyticsAttributes('DeploymentsExperienceSuccess');

	if (attributes === null) {
		return null;
	}

	return isExperienceSuccess(coreDataServiceResult) ? (
		<ExperienceSuccess
			experience={viewDeploymentsExperienceName}
			attributes={{
				...attributes,
				hasDeployments: Number(coreDataServiceResult.data?.allDeployments.length) > 0,
			}}
		/>
	) : null;
};
